import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, money, CURRENCIES, CURRENCY_OPTS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Stat, RagBadge, act } from "@/components/common";

function CurrencyControl({ p, reload }) {
  const { user } = useAuth();
  const [cur, setCur] = useState(p.currency);
  const canChange = ["pmo", "finance"].includes(user.role) && !p.locked;
  const save = () => {
    if (!window.confirm(`Change ${p.code} from ${p.currency} to ${cur}? Existing amounts are NOT converted — they will simply be shown in ${cur}.`)) return;
    act(api.put(`/projects/${p.id}/currency`, { currency: cur }), `Currency changed to ${cur}`).then((r) => r && reload());
  };
  return (
    <div data-testid="project-currency"><span className="text-slate-500">Currency:</span> <b data-testid="project-currency-code">{p.currency}</b> <span className="text-slate-500">({CURRENCIES[p.currency]?.[1]})</span>
      {canChange && (
        <span className="ml-2 inline-flex items-center gap-1">
          <select className="h-7 rounded border px-1 text-xs" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="project-currency-select">{CURRENCY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}</select>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={cur === p.currency} onClick={save} data-testid="project-currency-save">Change</Button>
        </span>)}
    </div>
  );
}

export default function Overview({ p, go, reload }) {
  const m = p.metrics;
  const cm = (n) => money(n, p.currency);
  return (
    <>
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="What to do next" testid="what-next-panel">
          <ul className="space-y-2">
            {p.next_actions.map((a, i) => (
              <li key={a.text}>
                {a.link ? <Link to={a.link} className="flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm hover:border-indigo-300" data-testid={`what-next-${i}`}>{a.text}<ArrowRight className="h-4 w-4 text-indigo-500" /></Link>
                  : <button onClick={() => go(a.tab)} data-testid={`what-next-${i}`} className="flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors">{a.text}<ArrowRight className="h-4 w-4 text-indigo-500" /></button>}
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="RAG engine" testid="rag-panel">
          <div className="grid grid-cols-2 gap-3">
            {["cost", "schedule", "risk", "compliance"].map((k) => <div key={k} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm capitalize">{k}<RagBadge value={p.rag?.[k]} testid={`rag-${k}`} /></div>)}
          </div>
          {p.rag_override && <div className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">PMO override → {p.rag_override.value}: {p.rag_override.justification} ({p.rag_override.by})</div>}
          <p className="mt-3 text-xs text-slate-500">Recomputed on every data change. Cost: ≤5% Green, 5–10% Amber, &gt;10% Red.</p>
        </Panel>
      </div>
      {m && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Stat label={p.baselined ? "Baseline budget" : "Planned budget"} value={cm(m.budget)} testid="ov-budget" />
          <Stat label="Actual" value={cm(m.actual)} testid="ov-actual" />
          <Stat label="Committed" value={cm(m.committed)} testid="ov-committed" />
          <Stat label="EAC" value={cm(m.eac)} testid="ov-eac" />
          <Stat label="Variance" value={`${m.variance_pct}%`} testid="ov-variance" />
        </div>
      )}
      <Panel title="About">
        <p className="text-sm text-slate-600">{p.description || "No description"}</p>
        <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <CurrencyControl p={p} reload={reload} />
          <div><span className="text-slate-500">Sponsor:</span> {p.sponsor_name || "—"}</div>
          <div><span className="text-slate-500">Dates:</span> {p.start || "?"} → {p.end || "?"}</div>
          <div><span className="text-slate-500">Steering committee:</span> {p.has_psc ? "Yes (Large)" : "No"}</div>
        </div>
      </Panel>
    </>
  );
}
